export const academicsObligationsModule = "academics-obligations" as const;

export const WHOLE_MAD_MIN = -(2n ** 63n);
export const WHOLE_MAD_MAX = 2n ** 63n - 1n;

declare const wholeMadBrand: unique symbol;
export type WholeMad = bigint & { readonly [wholeMadBrand]: "WholeMad" };

export class FinancialValidationError extends Error {
  constructor(
    readonly code:
      | "INVALID_WHOLE_MAD"
      | "WHOLE_MAD_OUT_OF_RANGE"
      | "INVALID_DATE"
      | "INVALID_OBLIGATION"
      | "INVALID_ALLOCATION",
  ) {
    super(code);
  }
}

export function wholeMad(value: string, options: { allowNegative?: boolean } = {}): WholeMad {
  if (typeof value !== "string" || !/^-?(?:0|[1-9][0-9]*)$/.test(value) || value === "-0")
    throw new FinancialValidationError("INVALID_WHOLE_MAD");
  const parsed = BigInt(value);
  if (parsed < WHOLE_MAD_MIN || parsed > WHOLE_MAD_MAX)
    throw new FinancialValidationError("WHOLE_MAD_OUT_OF_RANGE");
  if (!options.allowNegative && parsed < 0n)
    throw new FinancialValidationError("INVALID_WHOLE_MAD");
  return parsed as WholeMad;
}

export function wholeMadString(value: WholeMad): string {
  return value.toString(10);
}

export function addWholeMad(values: readonly WholeMad[]): WholeMad {
  const sum = values.reduce<bigint>((total, value) => total + value, 0n);
  return wholeMad(sum.toString(), { allowNegative: true });
}

export type DateOnly = string & { readonly __dateOnly: true };

export function dateOnly(value: string): DateOnly {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new FinancialValidationError("INVALID_DATE");
  const [year, month, day] = match.slice(1).map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    throw new FinancialValidationError("INVALID_DATE");
  return value as DateOnly;
}

function dateParts(value: DateOnly): [number, number, number] {
  return value.split("-").map(Number) as [number, number, number];
}

function utcDay(value: DateOnly): number {
  const [year, month, day] = dateParts(value);
  return Date.UTC(year, month - 1, day);
}

export function compareDateOnly(left: DateOnly, right: DateOnly): number {
  return left.localeCompare(right);
}

export interface AcademicCalendarSnapshot {
  readonly id: string;
  readonly academicYear: string;
  readonly activeFrom: DateOnly;
  readonly activeThrough: DateOnly;
  readonly weekendDays: readonly number[];
  readonly holidays: readonly DateOnly[];
  readonly timezone: "Africa/Casablanca";
}

export function isWorkingDay(calendar: AcademicCalendarSnapshot, value: DateOnly): boolean {
  const weekday = new Date(utcDay(value)).getUTCDay();
  return !calendar.weekendDays.includes(weekday) && !calendar.holidays.includes(value);
}

export function nextWorkingDay(calendar: AcademicCalendarSnapshot, value: DateOnly): DateOnly {
  let cursor = utcDay(value);
  for (let attempt = 0; attempt < 370; attempt += 1) {
    const candidate = dateOnly(new Date(cursor).toISOString().slice(0, 10));
    if (isWorkingDay(calendar, candidate)) return candidate;
    cursor += 86_400_000;
  }
  throw new FinancialValidationError("INVALID_DATE");
}

export function firstWorkingDayOfMonth(
  calendar: AcademicCalendarSnapshot,
  year: number,
  month: number,
): DateOnly {
  return nextWorkingDay(calendar, dateOnly(`${year}-${String(month).padStart(2, "0")}-01`));
}

export interface FeeSnapshot {
  readonly id: string;
  readonly family: ObligationFamily;
  readonly labelAr: string;
  readonly labelFr: string;
  readonly amountMad: WholeMad;
  readonly effectiveFrom: DateOnly;
  readonly scheduleVersion: string;
}

export type ObligationFamily = "registration" | "tuition" | "transport" | "accompaniment";
export type ObligationActionKind = "discount" | "waiver" | "adjustment";

export interface ObligationAction {
  readonly id: string;
  readonly kind: ObligationActionKind;
  readonly amountMad: WholeMad;
  readonly reason: string;
  readonly effectiveDate: DateOnly;
}

export function obligationActionAmount(input: {
  readonly kind: ObligationActionKind;
  readonly amountMad?: string;
  readonly remainingMad: string;
  readonly netMad: string;
  readonly settledMad: string;
  readonly reason: string;
}): WholeMad {
  const reason = input.reason.trim();
  if (reason.length < 3 || reason.length > 500)
    throw new FinancialValidationError("INVALID_OBLIGATION");
  const remaining = wholeMad(input.remainingMad);
  const net = wholeMad(input.netMad);
  const settled = wholeMad(input.settledMad);
  if (remaining !== net - settled || remaining <= 0n)
    throw new FinancialValidationError("INVALID_OBLIGATION");
  if (input.kind === "waiver") {
    if (input.amountMad !== undefined) throw new FinancialValidationError("INVALID_OBLIGATION");
    return remaining;
  }
  if (input.amountMad === undefined) throw new FinancialValidationError("INVALID_OBLIGATION");
  if (input.kind === "discount") {
    const amount = wholeMad(input.amountMad);
    if (amount <= 0n || amount > remaining)
      throw new FinancialValidationError("INVALID_OBLIGATION");
    return amount;
  }
  const adjustment = wholeMad(input.amountMad, { allowNegative: true });
  if (adjustment === 0n) throw new FinancialValidationError("INVALID_OBLIGATION");
  const adjustedNet = net + adjustment;
  if (
    adjustedNet < settled ||
    adjustedNet < 0n ||
    adjustedNet < WHOLE_MAD_MIN ||
    adjustedNet > WHOLE_MAD_MAX
  )
    throw new FinancialValidationError("INVALID_OBLIGATION");
  return adjustment;
}

export interface Obligation {
  readonly id: string;
  readonly studentId: string;
  readonly family: ObligationFamily;
  readonly period: string;
  readonly labelAr: string;
  readonly labelFr: string;
  readonly originalMad: WholeMad;
  readonly dueDate: DateOnly;
  readonly feeSnapshot: FeeSnapshot;
  readonly calendarSnapshotId: string;
  readonly actions: readonly ObligationAction[];
  readonly settledMad: WholeMad;
}

export interface ObligationState {
  readonly originalMad: WholeMad;
  readonly actionMad: WholeMad;
  readonly netMad: WholeMad;
  readonly settledMad: WholeMad;
  readonly remainingMad: WholeMad;
  readonly overdue: boolean;
}

export function deriveObligationState(obligation: Obligation, asOf: DateOnly): ObligationState {
  let actionTotal = 0n;
  for (const action of obligation.actions) {
    const amount = BigInt(action.amountMad);
    actionTotal += action.kind === "adjustment" ? amount : -amount;
  }
  const net = BigInt(obligation.originalMad) + actionTotal;
  const remaining = net - BigInt(obligation.settledMad);
  if (net < 0n || remaining < 0n) throw new FinancialValidationError("INVALID_OBLIGATION");
  return {
    originalMad: obligation.originalMad,
    actionMad: wholeMad(actionTotal.toString(), { allowNegative: true }),
    netMad: wholeMad(net.toString()),
    settledMad: obligation.settledMad,
    remainingMad: wholeMad(remaining.toString()),
    overdue: remaining > 0n && compareDateOnly(obligation.dueDate, asOf) < 0,
  };
}

export interface AllocationDraft {
  readonly obligationId: string;
  readonly amountMad: WholeMad;
}

export function proposeOldestDueAllocations(
  obligations: readonly Obligation[],
  amountMad: WholeMad,
  asOf: DateOnly,
): readonly AllocationDraft[] {
  let unallocated = BigInt(amountMad);
  const ordered = [...obligations].sort((left, right) => {
    const leftState = deriveObligationState(left, asOf);
    const rightState = deriveObligationState(right, asOf);
    if (leftState.overdue !== rightState.overdue) return leftState.overdue ? -1 : 1;
    return left.dueDate.localeCompare(right.dueDate) || left.id.localeCompare(right.id);
  });
  const allocations: AllocationDraft[] = [];
  for (const obligation of ordered) {
    if (unallocated === 0n) break;
    const remaining = BigInt(deriveObligationState(obligation, asOf).remainingMad);
    const allocated = remaining < unallocated ? remaining : unallocated;
    if (allocated > 0n) {
      allocations.push({ obligationId: obligation.id, amountMad: wholeMad(allocated.toString()) });
      unallocated -= allocated;
    }
  }
  if (unallocated > 0n) throw new FinancialValidationError("INVALID_ALLOCATION");
  return allocations;
}

export function validateAllocations(
  obligations: readonly Obligation[],
  allocations: readonly AllocationDraft[],
  paymentAmountMad: WholeMad,
  asOf: DateOnly,
): void {
  const byId = new Map(obligations.map((obligation) => [obligation.id, obligation]));
  const seen = new Set<string>();
  let total = 0n;
  for (const allocation of allocations) {
    if (seen.has(allocation.obligationId)) throw new FinancialValidationError("INVALID_ALLOCATION");
    seen.add(allocation.obligationId);
    const obligation = byId.get(allocation.obligationId);
    if (!obligation || BigInt(allocation.amountMad) <= 0n)
      throw new FinancialValidationError("INVALID_ALLOCATION");
    if (BigInt(allocation.amountMad) > BigInt(deriveObligationState(obligation, asOf).remainingMad))
      throw new FinancialValidationError("INVALID_ALLOCATION");
    total += BigInt(allocation.amountMad);
  }
  if (total !== BigInt(paymentAmountMad)) throw new FinancialValidationError("INVALID_ALLOCATION");
}
